import React, { useLayoutEffect, useState, useMemo, useRef } from 'react';
import { change, Field, formValueSelector, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Form, FormGroup, FormText } from 'reactstrap';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import FormItem from '../../../common/forms/FormItem';
import ItinerarySearchInput from '../../../common/forms/input/ItinerarySearchInput';
import SelectionableTable from '../../../common/forms/table/SelectionableTable';
import {
  BAGGAGE_MANIFEST_ACTIONS,
  BAGGAGE_MANIFEST_MODULE,
  DEFAULT_PAGE_SIZE,
  NEW_BAGGAGE_ITEM_STATUS,
} from '../../../../config/constants';
import {
  clearBaggageItems,
  getItemsByItineraryId,
} from '../../../../actions/baggage/Item';
import { generateBaggageItemManifestColumns } from '../../../../config/columns';
import {
  clearLocations,
  getBaggageManifest,
  getLocationByItineraryId,
} from '../../../../actions';
import { printBaggageManifest } from '../../../../utils/printers/BaggageManifest';
import BaggageManifestDefaultButtons from './baggage-manifest/BaggageManifesDefaultButtons';
import BaggageManifestBoardingSection from './baggage-manifest/BaggageManifestBoardingSection';
import BaggageManifestDismissalSection from './baggage-manifest/BaggageManifestDismissalSection';
import BaggageManifestChangeSection from './baggage-manifest/BaggageManifestChangeSection';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import {
  clearBaggageManifestPrint,
  getBaggageManifestPrintByItineraryId,
} from '../../../../actions/baggage/BaggageManifestPrint';
import LocationSelect from '../../../common/forms/select/LocationSelect';
import { isRequired } from '../../../../utils/validators';

export const BAGGAGE_MANIFEST_FORM = 'BaggageManifestForm';

const selector = formValueSelector(BAGGAGE_MANIFEST_FORM);

const BaggageManifestForm = ({
  handleSubmit,
  dispatchGetItemsByItineraryId,
  items,
  dispatchClearBaggageItems,
  dispatchClearLocations,
  loading,
  dispatchGetBaggageManifest,
  manifestLoading,
  dispatchChange,
  dispatchGetLocationByItineraryId,
  onSubmit,
  itemActivityLoading,
  actionValue,
  dispatchClearBaggageManifestPrint,
  dispatchGetBaggageManifestPrintByItineraryId,
  isManifestPrinted,
  locationLoading,
}) => {
  useLayoutEffect(
    () => () => {
      dispatchClearBaggageItems();
      dispatchClearLocations();
      dispatchClearBaggageManifestPrint();
    },
    [],
  );

  const statusOptions = useMemo(
    () =>
      [...new Map(items.map(({ status }) => [status, status])).values()].map(
        (status) => ({
          value: status,
          label: NEW_BAGGAGE_ITEM_STATUS[status]
            ? NEW_BAGGAGE_ITEM_STATUS[status].label
            : status,
        }),
      ),
    [items],
  );

  const sourceOptions = useMemo(
    () =>
      [
        ...new Map(
          items.map(({ sourceLocation }) => [
            sourceLocation.id,
            sourceLocation,
          ]),
        ).values(),
      ].map(({ id, name }) => ({ value: id, label: name })),
    [items],
  );

  const destinationOptions = useMemo(
    () =>
      [
        ...new Map(
          items.map(({ destinationLocation }) => [
            destinationLocation.id,
            destinationLocation,
          ]),
        ).values(),
      ].map(({ id, name }) => ({ value: id, label: name })),
    [items],
  );

  const columns = useMemo(
    () =>
      generateBaggageItemManifestColumns(
        sourceOptions,
        destinationOptions,
        statusOptions,
      ),
    [sourceOptions, destinationOptions, statusOptions],
  );

  const [activeModule, setActiveModule] = useState(
    Object.values(BAGGAGE_MANIFEST_MODULE).find((m) => m.default).value,
  );

  const [tableResetKey, setTableResetKey] = useState(0);

  const isDefaultModule =
    activeModule === BAGGAGE_MANIFEST_MODULE.DEFAULT.value;

  const [disableModuleButtons, setDisableModuleButtons] = useState(true);
  const [selectedItineraryId, setSelectedItineraryId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const firstRenderRef = useRef(true);

  useLayoutEffect(() => {
    if (!selectedItineraryId && firstRenderRef.current) {
      dispatchClearBaggageItems();
      firstRenderRef.current = false;
    }
  }, [items]);

  const handleReload = () => {
    if (!selectedItineraryId) return;
    dispatchGetLocationByItineraryId({
      itineraryId: selectedItineraryId,
      showOnlyAssigned: true,
    });
    const searchParams = {
      itineraryId: selectedItineraryId,
      locationId: selectedLocationId,
    };
    dispatchGetItemsByItineraryId(searchParams);
    dispatchGetBaggageManifestPrintByItineraryId(searchParams);
  };

  const handleSelectItinerary = async (itinerary) => {
    if (!itinerary) return;
    const { itineraryId } = itinerary;
    setSelectedItineraryId(itineraryId);
    dispatchGetLocationByItineraryId({ itineraryId, showOnlyAssigned: true });
    dispatchChange(BAGGAGE_MANIFEST_FORM, 'location', null);
  };

  const handleLocationChange = async (location) => {
    if (!location) return;
    const { value: locationId } = location;
    const searchParams = {
      itineraryId: selectedItineraryId,
      locationId,
    };
    const response = await dispatchGetItemsByItineraryId(searchParams);
    if (!response) return;
    dispatchGetBaggageManifestPrintByItineraryId(searchParams);
    setDisableModuleButtons(false);
    setSelectedLocationId(locationId);
  };

  const handleClearItinerary = () => {
    dispatchClearBaggageItems();
    setDisableModuleButtons(true);
    setSelectedItineraryId(null);
    dispatchClearLocations();
    dispatchClearBaggageManifestPrint();
    dispatchChange(BAGGAGE_MANIFEST_FORM, 'location', null);
  };

  const handlePrintBaggageManifest = async () => {
    if (!selectedItineraryId) return;

    const manifest = await dispatchGetBaggageManifest({
      itineraryId: selectedItineraryId,
      locationId: selectedLocationId,
    });

    if (manifest) {
      manifest.itineraryId = selectedItineraryId;
      printBaggageManifest(manifest);
    }

    await dispatchGetBaggageManifestPrintByItineraryId({
      itineraryId: selectedItineraryId,
      locationId: selectedLocationId,
    });
  };

  const handleBaggageManifestModuleChange = (module) => {
    setActiveModule(module);

    if (module === BAGGAGE_MANIFEST_MODULE.BOARDING.value) {
      dispatchChange(
        BAGGAGE_MANIFEST_FORM,
        'action',
        BAGGAGE_MANIFEST_ACTIONS.DISMISS,
      );
      return;
    }

    if (module === BAGGAGE_MANIFEST_MODULE.CHANGE_ITINERARY.value) {
      dispatchChange(
        BAGGAGE_MANIFEST_FORM,
        'action',
        BAGGAGE_MANIFEST_ACTIONS.CHANGE_ITINERARY,
      );
      return;
    }

    dispatchChange(BAGGAGE_MANIFEST_FORM, 'action', null);
  };

  const onHandleSubmit = async (formValues) => {
    if (!selectedItineraryId) {
      toastr.error('Error', 'Debe seleccionar un itinerario para continuar');
      return;
    }
    const formItems = formValues.items || [];
    const itemIds = formItems.map(({ id }) => id);

    const payload = {
      ...formValues,
      itineraryId: selectedItineraryId,
      items: itemIds,
      itemObjects: formItems,
    };

    await onSubmit(payload);
    setTableResetKey((prev) => prev + 1);
  };

  const isItemSelectable = (item) => {
    const { status } = item;
    if (
      isManifestPrinted &&
      activeModule === BAGGAGE_MANIFEST_MODULE.DEFAULT.value
    )
      return false;
    if (status === NEW_BAGGAGE_ITEM_STATUS.DELIVERED.value) return false;
    return true;
  };

  const saveButtonDisabled =
    manifestLoading || loading || items.length === 0 || itemActivityLoading;

  const disableManifestButtons =
    loading || manifestLoading || !selectedItineraryId || !selectedLocationId;

  return (
    <Form onSubmit={handleSubmit(onHandleSubmit)}>
      <FormGroup row>
        <FormItem label="Itinerario">
          <Field
            name="itineraryId"
            component={ItinerarySearchInput}
            placeholder="Itinerario"
            handleSelectItinerary={handleSelectItinerary}
            handleClear={handleClearItinerary}
            props={{
              form: BAGGAGE_MANIFEST_FORM,
            }}
            clearOnUnmountLocation={false}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Ubicación Actual" required>
          <Field
            name="location"
            component={LocationSelect}
            validate={[isRequired]}
            isDisabled={!selectedItineraryId || locationLoading}
            onChange={handleLocationChange}
          />
          {!selectedLocationId && (
            <FormText>
              Selecciona la ubicación para cargar el manifiesto
            </FormText>
          )}
        </FormItem>
      </FormGroup>
      <div>
        <ButtonGroup
          className={
            BAGGAGE_MANIFEST_MODULE.DEFAULT.value !== activeModule ? 'mb-3' : ''
          }
        >
          {Object.values(BAGGAGE_MANIFEST_MODULE).map(({ value, label }) => (
            <Button
              key={value}
              color="secondary"
              outline={activeModule !== value}
              onClick={() => handleBaggageManifestModuleChange(value)}
              disabled={disableModuleButtons}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      {BAGGAGE_MANIFEST_MODULE.DEFAULT.value === activeModule && (
        <BaggageManifestDefaultButtons
          selectedItineraryId={selectedItineraryId}
          disableAddItemButton={
            disableManifestButtons || itemActivityLoading || locationLoading
          }
          disablePrintButton={disableManifestButtons}
          disableReloadButton={disableManifestButtons}
          handlePrintBaggageManifest={handlePrintBaggageManifest}
          handleReload={handleReload}
          isManifestPrinted={isManifestPrinted}
        />
      )}
      <SelectionableTable
        key={tableResetKey}
        columns={columns}
        data={items}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        keyField="id"
        loading={loading}
        form={BAGGAGE_MANIFEST_FORM}
        returnOnlySelectedItems
        returnFullSelectedObjects
        disabled={isDefaultModule}
        showSelectAll={!isDefaultModule}
        filterable
        defaultFilterMethod={filterMethodCaseInsensitive}
        isItemSelectable={isItemSelectable}
      />
      <br />
      {BAGGAGE_MANIFEST_MODULE.BOARDING.value === activeModule && (
        <BaggageManifestBoardingSection
          form={BAGGAGE_MANIFEST_FORM}
          isManifestPrinted={isManifestPrinted}
          saveButtonDisabled={saveButtonDisabled}
        />
      )}
      {BAGGAGE_MANIFEST_MODULE.DISMISSAL.value === activeModule && (
        <BaggageManifestDismissalSection
          form={BAGGAGE_MANIFEST_FORM}
          actionValue={actionValue}
          saveButtonDisabled={saveButtonDisabled}
        />
      )}
      {BAGGAGE_MANIFEST_MODULE.CHANGE_ITINERARY.value === activeModule && (
        <BaggageManifestChangeSection
          form={BAGGAGE_MANIFEST_FORM}
          isManifestPrinted={isManifestPrinted}
          saveButtonDisabled={saveButtonDisabled}
        />
      )}
    </Form>
  );
};

BaggageManifestForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatchGetItemsByItineraryId: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dispatchClearBaggageItems: PropTypes.func.isRequired,
  dispatchGetBaggageManifest: PropTypes.func.isRequired,
  dispatchGetLocationByItineraryId: PropTypes.func.isRequired,
  itineraryIdValue: PropTypes.shape({}),
  loading: PropTypes.bool,
  manifestLoading: PropTypes.bool,
  dispatchChange: PropTypes.func.isRequired,
  dispatchClearLocations: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  itemActivityLoading: PropTypes.bool,
  locationLoading: PropTypes.bool,
  actionValue: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  dispatchClearBaggageManifestPrint: PropTypes.func.isRequired,
  dispatchGetBaggageManifestPrintByItineraryId: PropTypes.func.isRequired,
  isManifestPrinted: PropTypes.bool,
};

BaggageManifestForm.defaultProps = {
  itineraryIdValue: undefined,
  loading: false,
  manifestLoading: false,
  itemActivityLoading: false,
  actionValue: undefined,
  isManifestPrinted: false,
  locationLoading: false,
};

const mapStateToProps = (state) => ({
  items: state.BaggageUnit.Item.getIn(['content', 'content', 'content']),
  loading: state.BaggageUnit.Item.getIn(['content', 'loading']),
  itineraryIdValue: selector(state, 'itineraryId'),
  manifestLoading: !state.ItineraryUnit.Itinerary.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  itemActivityLoading: !state.BaggageUnit.Item.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  actionValue: selector(state, 'action'),
  isManifestPrinted: !state.BaggageUnit.BaggageManifestPrint.getIn([
    'current',
    'content',
  ]).isEmpty(),
  locationLoading: state.LocationUnit.Location.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchGetItemsByItineraryId: getItemsByItineraryId,
  dispatchClearBaggageItems: clearBaggageItems,
  dispatchGetBaggageManifest: getBaggageManifest,
  dispatchGetLocationByItineraryId: getLocationByItineraryId,
  dispatchChange: change,
  dispatchClearLocations: clearLocations,
  dispatchClearBaggageManifestPrint: clearBaggageManifestPrint,
  dispatchGetBaggageManifestPrintByItineraryId:
    getBaggageManifestPrintByItineraryId,
};

const form = reduxForm({
  form: BAGGAGE_MANIFEST_FORM,
})(BaggageManifestForm);

export default connect(mapStateToProps, mapDispatchToProps)(form);
